import { colors, typography } from "Shared/tokens"
import React from "react"
import { StyledTypography } from "./Typography.style"

// ─── Tag mapping ────────────────────────────────────────────────────────────

const HEADING_TAG_MAP: Record<HeadingVariant, string> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
}

const TEXT_TAG_MAP: Record<TextVariant, string> = {
  body: "p",
  caption: "span",
}

// ─── Color resolution ───────────────────────────────────────────────────────

const COLOR_PRESETS: Record<string, string> = {
  primary: colors.textPrimary,
  secondary: colors.textSecondary,
  muted: colors.textMuted,
}

const resolveColor = (color?: string): string | undefined => {
  if (!color) return undefined
  return COLOR_PRESETS[color] || color
}

// ─── Types ──────────────────────────────────────────────────────────────────

type HeadingVariant = "h1" | "h2" | "h3"
type TextVariant = "body" | "caption"

interface TypographyOwnProps {
  as?: React.ElementType
  color?: "primary" | "secondary" | "muted" | string
  weight?: number
  uppercase?: boolean
  truncate?: boolean
  lineClamp?: number
  align?: "left" | "center" | "right"
}

export interface HeadingProps
  extends TypographyOwnProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  variant: HeadingVariant
}

export interface TextProps
  extends TypographyOwnProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  variant?: TextVariant
}

// ─── Heading ────────────────────────────────────────────────────────────────

export const Heading = React.forwardRef<HTMLElement, HeadingProps>(
  (
    {
      variant,
      as,
      color,
      weight,
      uppercase,
      truncate,
      lineClamp,
      align,
      ...rest
    },
    ref
  ) => {
    const spec = typography[variant]
    const Tag = as || HEADING_TAG_MAP[variant]

    return (
      <StyledTypography
        as={Tag}
        ref={ref}
        $size={spec.size}
        $weight={weight ?? spec.weight}
        $lineHeight={spec.lineHeight}
        $color={resolveColor(color)}
        $uppercase={uppercase}
        $truncate={truncate}
        $lineClamp={lineClamp}
        $align={align}
        {...rest}
      />
    )
  }
)

Heading.displayName = "Heading"

// ─── Text ───────────────────────────────────────────────────────────────────

export const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      variant = "body",
      as,
      color,
      weight,
      uppercase,
      truncate,
      lineClamp,
      align,
      ...rest
    },
    ref
  ) => {
    const spec = typography[variant]
    const Tag = as || TEXT_TAG_MAP[variant]

    return (
      <StyledTypography
        as={Tag}
        ref={ref}
        $size={spec.size}
        $weight={weight ?? spec.weight}
        $lineHeight={spec.lineHeight}
        $color={resolveColor(color)}
        $uppercase={uppercase}
        $truncate={truncate}
        $lineClamp={lineClamp}
        $align={align}
        {...rest}
      />
    )
  }
)

Text.displayName = "Text"
